import React, { useState } from "react";
import axios from "axios";
import { API_URL } from "../../creds";
import { FaImage } from "react-icons/fa";
import { Tooltip } from "react-tooltip";
import { toast } from "react-toastify";
import { Language, SampleTexts } from "../../utils/Language";
import { Engine } from "../../utils/Engine";
import { Document, Packer, Paragraph, TextRun } from "docx";
import { saveAs } from "file-saver";
import IconsSubmit from '../../components/styled/IconsSubmit'
import TranslationTextEdit from "./TranslationTextEdit";
import ImageUploadModal from "./ImageUploadModal";

// Language selection component
const LanguageSelector = ({ inputLang, outputLang, setInputLang, setOutputLang, setFileContent, setResponseData, fileContent, responseData }) => {
  const sortedLanguages = [...Language].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="flex space-x-2 ml-10">
      <select
        name="InputLanguage"
        className="bg-white border border-gray-300 rounded-lg shadow-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 pl-8 pr-2 py-2"
        value={inputLang}
        onChange={(e) => setInputLang(e.target.value)}
      >
        <option disabled value="">Source</option>
        {sortedLanguages.map((lang) => (
          <option key={lang.code} value={lang.code}>
            {lang.name}
          </option>
        ))}
      </select>
      <button
        onClick={() => {
          setInputLang(outputLang);
          setOutputLang(inputLang);
          setFileContent(responseData);
          setResponseData(fileContent);
        }}
        className="bg-white-500 font-xlarge text-black font-xbold py-2 rounded"
      >
        ⇆
      </button>
      <select
        name="OutputLanguage"
        className="bg-white border border-gray-300 rounded-lg shadow-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 pl-8 py-2"
        value={outputLang}
        onChange={(e) => {
          setOutputLang(e.target.value);
          setResponseData("");
        }}
      >
        <option disabled value="">Target</option>
        {sortedLanguages.map((lang) => (
          <option key={lang.code} value={lang.code}>
            {lang.name}
          </option>
        ))}
      </select>
    </div>
  );
};

// Engine selection component
const EngineSelector = ({ selectedEngine, setSelectedEngine, setResponseData }) => {
  const sortedEngines = [...Engine].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="relative">
      <label className="ml-20">Engine: </label>
      <select
        name="Engine"
        className="bg-white border border-gray-300 rounded-lg shadow-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 pl-8 pr-4 py-2"
        value={selectedEngine}
        onChange={(e) => {
          setSelectedEngine(e.target.value);
          setResponseData("");
        }}
      >
        <option disabled value="">Select Engine</option>
        {sortedEngines.map((engine) => (
          <option key={engine.code} value={engine.code}>
            {engine.name}
          </option>
        ))}
      </select>
    </div>
  );
};

// Sample text selector component
// eslint-disable-next-line
const SampleTextSelector = ({ setFileContent, setInputLang }) => (
  <select
    className="bg-white border border-gray-300 rounded-lg shadow-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 pl-8 pr-4 py-2"
    onChange={(e) => {
      const [content, lang] = e.target.value.split("|");
      setFileContent(content);
      setInputLang(lang);
    }}
  >
    <option value="">Select Sample</option>
    {SampleTexts.map((sample) => (
      <option key={sample.lang} value={`${sample.text}|${sample.lang}`}>
        Sample {sample.name}
      </option>
    ))}
  </select>
);

// Print options component
const PrintOptions = ({ responseData }) => {
  const [selectedFormat, setSelectedFormat] = useState("");

  const handlePrint = (format) => {
    if (!responseData) {
      toast.error("No translation available to print.");
      setSelectedFormat("");
      return;
    }

    try {
      switch (format) {
        case "txt":
          const txtBlob = new Blob([responseData], { type: "text/plain" });
          const txtUrl = URL.createObjectURL(txtBlob);
          const txtLink = document.createElement("a");
          txtLink.href = txtUrl;
          txtLink.download = "translation.txt";
          txtLink.click();
          URL.revokeObjectURL(txtUrl);
          break;

        case "doc":
          const doc = new Document({
            sections: [
              {
                properties: {},
                children: [
                  new Paragraph({
                    children: [new TextRun(responseData)],
                  }),
                ],
              },
            ],
          });
          Packer.toBlob(doc).then((blob) => {
            saveAs(blob, "translation.docx");
          });
          break;

        default:
          toast.error("Invalid print format selected.");
          break;
      }
      setSelectedFormat(""); // Reset to allow re-selection of the same format
    } catch (error) {
      console.error("Print error:", error);
      toast.error("Failed to generate print file. Please try again.");
      setSelectedFormat("");
    }
  };

  return (
    <div className="relative">
      <select
        className="bg-white border border-gray-300 rounded-lg shadow-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 pl-8 pr-4 py-2"
        value={selectedFormat}
        onChange={(e) => handlePrint(e.target.value)}
      >
        <option value="">Print Translation</option>
        <option value="txt">Print as TXT</option>
        <option value="doc">Print as DOC</option>
      </select>
    </div>
  );
};


// File upload modal component
const FileUploadModal = ({ isOpen, onClose, setLoading, inputLang, selectedEngine, setFileContent, setFileName, isLoading, setTaskID }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const access_token = localStorage.getItem('access_token');
  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      toast.error("Please select a file to upload.");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("src", inputLang || "auto"); // Use 'auto' if no source language selected
      formData.append("engine", selectedEngine || "google"); // Default to 'google' if no engine selected

      const response = await axios.post(`${API_URL}/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          'Authorization': `Bearer ${access_token}`
        },
      });

      const data = response.data;
      if (data.content) {
        // const content = data.content.substring(0, Math.min(data.content.length, 5000));
        const content = data.content
        const taskid = data.taskid
        setFileContent(content);
        setFileName(selectedFile.name);
        setTaskID(taskid);
        toast.success("File uploaded successfully.");
      } else {
        toast.error("Failed to process file content.");
      }
    } catch (error) {
      console.error("File upload error:", error);
      toast.error("Failed to upload file. Please try again.");
    } finally {
      setLoading(false);
      onClose(); // Close modal after submission
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
        <h2 className="text-xl font-bold mb-4">Upload Documents</h2>
        <input
          type="file"
          name="file_upload"
          className="mb-4 w-full"
          accept=".txt"
          onChange={handleFileChange}
        />
        <button
          onClick={handleSubmit}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mr-2"
          disabled={isLoading}
        >
          {isLoading ? <IconsSubmit /> : "Submit"}
        </button>
        <button
          onClick={onClose}
          className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const Translation = () => {
  // eslint-disable-next-line
  const [file, setFile] = useState(null);
  const [taskID, setTaskID] = useState(null);
  const [fileContent, setFileContent] = useState("");
  // eslint-disable-next-line
  const [fileName, setFileName] = useState("");
  const [responseData, setResponseData] = useState("");
  const [inputLang, setInputLang] = useState("");
  const [outputLang, setOutputLang] = useState("");
  const [selectedEngine, setSelectedEngine] = useState("Bhashini");
  const [isLoading, setLoading] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const isUrdu = inputLang === "ur" || inputLang === "ks" || inputLang === "sd";
  const isUrdu_o = outputLang === "ur" || outputLang === "ks" || outputLang === "sd";
  const access_token = localStorage.getItem('access_token');

  // eslint-disable-next-line
  const clearData = () => {
    setFileContent("");
    setResponseData("");
    setFileName("");
    setFile(null);
  };

  const handleSubmit = async () => {
    setResponseData("");
    if (!inputLang) {
      toast.error("Please select a source language.");
      return;
    }
    if (!outputLang) {
      toast.error("Please select a target language.");
      return;
    }
    if (inputLang === outputLang) {
      toast.error("Source and Target Languages cannot be same.");
      return;
    }
    if (!selectedEngine) {
      toast.error("Please select a translation engine.");
      return;
    }
    if (fileContent === "") {
      toast.error("Please enter source text or upload a file.");
      return;
    }

    toast.info("Processing, Please Wait...", { autoClose: 1500, hideProgressBar: false });
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/translation`, {
        src: inputLang,
        tar: outputLang,
        inp: fileContent,
        engine: selectedEngine,
      }, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${access_token}`
        },
      });

      const data = response.data;
      const target = data.output;
      const status = data.status;
      const storyID = data.story_id;
      setTaskID(storyID)
      toast.dismiss();
      setResponseData(target);
      console.log(target, status);
    } catch (error) {
      console.error("Error:", error);
      setTimeout(() => {
        toast.dismiss();
        toast.error("Server down, Please try after sometime.");
      }, 1000);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!fileContent || !responseData) {
      toast.error("Please provide source and target text to save.");
      return;
    }
    setSaving(true);

    try {
      const response = await axios.post(`${API_URL}/save_task`, {
        source: fileContent,
        target: responseData,
        srcLang: inputLang,
        tgtLang: outputLang,
        engine: selectedEngine,
        story_id: taskID
      }, {
        headers: {
          "Content-Type": "application/json",
          'Authorization': `Bearer ${access_token}`
        },
      });

      toast.success(response.data.message);
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save files. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <section className="min-h-screen">
        <Tooltip id="my-tooltip" />
        <ImageUploadModal
          isOpen={showImageModal}
          onClose={() => {
            setShowImageModal(false);
            // Remove this line completely! It was blocking the update
            // setFileContent(fileContent);  ← DELETE THIS LINE!
          }}
          onTextExtracted={(text) => {
            setFileContent(text); // ← This correctly sets the extracted text
            // Optional: Show success
            toast.success("Text loaded – ready to translate!");
          }}
        />

        <FileUploadModal
          isOpen={showFileUploadModal}
          onClose={() => setShowFileUploadModal(false)}
          setLoading={setLoading}
          inputLang={inputLang}
          selectedEngine={selectedEngine}
          setFileContent={setFileContent}
          setFileName={setFileName}
          isLoading={isLoading}
          setTaskID={setTaskID}
        />
        <div className="flex flex-col px-10 pt-3">
          <div className="flex items-center justify-between rounded-lg relative mb-2">
            <div>
              <button
                onClick={() => setShowFileUploadModal(true)}
                className="bg-white hover:bg-gray-300 border border-gray-300 text-gray-900 py-2 px-2 rounded"
              >
                Documents
              </button>
              <button
                onClick={() => setShowImageModal(true)}
                className="bg-white hover:bg-gray-300 border border-gray-300 text-gray-900 py-2 px-4 rounded-lg ml-3"
              >
                <FaImage className="inline mr-2" /> Image
              </button>
            </div>


            <div className="flex space-x-4">
              <LanguageSelector
                inputLang={inputLang}
                outputLang={outputLang}
                setInputLang={setInputLang}
                setOutputLang={setOutputLang}
                setFileContent={setFileContent}
                setResponseData={setResponseData}
                fileContent={fileContent}
                responseData={responseData}
              />
              <EngineSelector
                selectedEngine={selectedEngine}
                setSelectedEngine={setSelectedEngine}
                setResponseData={setResponseData}
              />
            </div>
            <div className="flex items-center">
              {/* <button
              onClick={clearData}
              className="text-black font-bold py-2 px-4 rounded mr-4"
            >
            Clear
            </button> */}
              <button
                className={`bg-blue-500 hover:bg-green-700 text-white font-bold py-3 px-8 rounded-full ${isLoading ? "opacity-50 cursor-not-allowed bg-green-700" : ""}`}
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? <IconsSubmit /> : "Translate"}
              </button>
            </div>
          </div>

          <TranslationTextEdit
            isUrdu={isUrdu}
            isUrdu_o={isUrdu_o}
            fileContent={fileContent}
            setFileContent={setFileContent}
            responseData={responseData}
            setResponseData={setResponseData}
            setFile={setFile}
            setFileName={setFileName}
            targetLang={outputLang}
            inputLang={inputLang}
          // toast={toast} // pass if using toast for notifications

          />
          <div className="mt-4 flex justify-end">
            {/* <div className="col-span-6">
              <SampleTextSelector setFileContent={setFileContent} setInputLang={setInputLang} />
            </div> */}
            <div className="flex space-x-4">
              <button
                onClick={handleSave}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-full transition disabled:opacity-60"              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
              <PrintOptions responseData={responseData} />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Translation;